import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import TextSection from "../components/TextSection.tsx";
import ProfilePicture from "../components/ProfilePicture";
import Summary from "../components/Summary";
import CarouselSection from "../components/CarouselSection.tsx";
import Stack from "@mui/material/Stack";
import {Dayjs} from "dayjs";
import Menu, {MenuHandle, ListItemText} from "../components/Menu.tsx";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import List from "@mui/material/List";
import {useAuth} from "../lib/auth.ts";
import Box from "@mui/material/Box";
import {PROFILE_PICTURE_PLACEHOLDER} from "../constants/profile.ts";
import EditBottomBar from "../components/EditBottomBar.tsx";
import apiClient from "../lib/api.ts";
import {useParams} from "react-router";
import HomeButton from "../components/Menu/HomeButton.tsx";
import QRCodeButton from "../components/Menu/QRCodeButton.tsx";
import VideoSection from "../components/VideoSection.tsx";
import {useFeedback} from "@memaro/feedback";
import omit from "lodash-es/omit"
import {SaveProfileInput, Section as APISection, APIError} from "@memaro/api";
import PublicToggle from "../components/Menu/PublicToggle.tsx";
import ProfileLoader from "../components/ProfileLoader.tsx";
import ProfileNotFound from "../components/ProfileNotFound.tsx";
import ProfileUnknownError from "../components/ProfileUnknownError.tsx";
import GroupMembershipButton from "../components/Menu/GroupMembershipButton.tsx";
import SubscriptionButton from "../components/Menu/SubscriptionButton.tsx";
import ProfileBlocked from "../components/ProfileBlocked.tsx";
import AudioSection from "../components/AudioSection.tsx";
import TrialConfirmation from "../components/TrialConfirmation.tsx";
import {Divider} from "@memaro/components/Menu.tsx";
import {AdminGroupID} from "../constants/groups.ts";
import AddSectionButtonGrid from "../components/AddSectionButtonGrid.tsx";
import {useTheme} from "@memaro/themes";
import ShareButtons from "../components/Menu/ShareButtons.tsx";
import PhotoWithQRCodeButton from "../components/Menu/PhotoWithQRCodeButton.tsx";
import {Plan} from "@memaro/api";

type BaseSection = {
    sectionID: string
    uncollapse: boolean
    collapse: boolean
    isPublic: boolean
}

type TextSection = BaseSection & {
    type: "text";
    text: string;
}

const isTextSection = (s: Section): s is TextSection => s.type == 'text'

type PhotoCarouselSection = BaseSection & {
    type: "photo_carousel";
    photos: string[];
}

const isPhotoCarouselSection = (s: Section): s is PhotoCarouselSection => s.type == 'photo_carousel'

type VideoSection = BaseSection & {
    type: "video";
    video?: string;
}

const isVideoSection = (s: Section): s is VideoSection => s.type == 'video'

type AudioSection = BaseSection & {
    type: "audio";
    src?: string;
}

const isAudioSection = (s: Section): s is AudioSection => s.type == 'audio'

type Section = TextSection | PhotoCarouselSection | VideoSection | AudioSection

const randomString = () => (Math.random() + 1).toString(36).substring(7);

const ConfirmOverride = () => (
    <>
        <p>
            De herinneringspagina is in de tussentijd door iemand anders aangepast, als u doorgaat overschrijft u die
            gemaakte wijzigingen.
        </p>
        <p>
            Weet u zeker dat u door wilt gaan?"
        </p>
    </>
)

function MemorialPage() {
    const {state} = useAuth()
    const {profileID} = useParams()
    const {themeName, setThemeByName} = useTheme()
    const [groupIDs, setGroupIDs] = useState<string[]>([])
    const [profilePicture, setProfilePicture] = useState<string | undefined>()
    const [etag, setEtag] = useState<string | undefined>()
    const [name, setName] = useState<string | undefined>()
    const [isPublic, setIsPublic] = useState<boolean>(false)
    const [isBlocked, setIsBlocked] = useState<boolean>(false)
    const [plan, setPlan] = useState<Plan | undefined>(undefined)
    const [dateOfBirth, setDateOfBirth] = useState<Dayjs | null>(null)
    const [dateOfDeath, setDateOfDeath] = useState<Dayjs | null>(null)
    const [sections, setSections] = useState<Section[]>([])
    const [editMode, setEditMode] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [err, setErr] = useState<'not_found' | 'unknown' | undefined>(undefined)
    const [saving, setSaving] = useState<boolean>(false)
    const menuRef = useRef<MenuHandle>(null)
    const {confirm, error, success} = useFeedback()

    useEffect(() => {
        setEditMode(false)
        if (state == "initialising") {
            return
        }
        Promise.all([
            apiClient.getProfile(profileID || "")
                .then(profile => {
                    setEtag(profile.etag)
                    setName(profile.name)
                    setProfilePicture(profile.profilePicture)
                    setIsPublic(profile.isPublic)
                    setIsBlocked(profile.isBlocked)
                    setPlan(profile.plan)
                    setDateOfBirth(profile.dateOfBirth ?? null)
                    setDateOfDeath(profile.dateOfDeath ?? null)
                    setSections((profile.sections ?? []).map(section => ({
                        ...section,
                        sectionID: randomString(),
                        uncollapse: false,
                        collapse: false,
                    })))
                    setThemeByName(profile.theme ?? 'default')
                })
                .catch((e) => {
                    setName(undefined)
                    setProfilePicture(undefined)
                    setIsPublic(false)
                    setIsBlocked(false)
                    setPlan(undefined)
                    setDateOfBirth(null)
                    setDateOfDeath(null)
                    setSections([])
                    setErr(e instanceof APIError && e.statusCode == 404 ? 'not_found' : 'unknown')
                }),
            apiClient.getGroups(profileID || "")
                .then(setGroupIDs)
                .catch(() => setErr('unknown'))
        ]).finally(() => setLoading(false))
    }, [profileID, setThemeByName, state])

    const isAdmin = useMemo(() => groupIDs.includes(AdminGroupID), [groupIDs])

    const enableEditMode = useCallback(() => setEditMode(true), [])
    const disableEditMode = useCallback(() => setEditMode(false), [])

    const handleSectionMoveUp = useCallback((sectionID: string) => setSections((prev) => {
        const index = prev.findIndex((section) => section.sectionID == sectionID)
        const newState = [...prev];
        newState[index] = {...prev[index], collapse: true}
        newState.splice(index - 1, 0, {...prev[index], sectionID: randomString(), uncollapse: true})
        return newState;
    }), [])

    const handleSectionMoveDown = useCallback((sectionID: string) => setSections((prev) => {
        const index = prev.findIndex((section) => section.sectionID == sectionID)
        const newState = [...prev];
        newState[index] = {...prev[index], collapse: true}
        newState.splice(index + 2, 0, {...prev[index], sectionID: randomString(), uncollapse: true})
        return newState;
    }), [])

    const handleSectionDelete = useCallback((sectionID: string) => setSections(
        (prev) => prev.map(
            (section) => section.sectionID == sectionID ? {...section, collapse: true} : section
        )
    ), [])

    const handleSectionCollapsed = useCallback((sectionID: string) => setSections(
        (prev) => prev.filter(section => section.sectionID != sectionID)
    ), [])

    const handleSectionChangeIsPublic = useCallback((sectionID: string, isPublic: boolean) => setSections(
        (prev) => prev.map(section => section.sectionID == sectionID ? {...section, isPublic} : section)
    ), [])

    const handleSectionTextChanged = useCallback((sectionID: string, text: string) => setSections(
        (prev) => prev.map(section => section.sectionID == sectionID ? {...section, text} : section)
    ), [])

    const handleSectionCarouselChanged = useCallback((sectionID: string, photos: string[]) => setSections(
        (prev) => prev.map(section => section.sectionID == sectionID ? {...section, photos} : section)
    ), [])

    const handleSectionVideoChanged = useCallback((sectionID: string, video: string) => setSections(
        (prev) => prev.map(section => section.sectionID == sectionID ? {...section, video} : section)
    ), [])

    const handleSectionAudioChanged = useCallback((sectionID: string, src: string) => setSections(
        (prev) => prev.map(section => section.sectionID == sectionID ? {...section, src} : section)
    ), [])

    const handleSave = useCallback(() => {
        setSaving(true)
        const profile: SaveProfileInput = {
            etag,
            name,
            dateOfBirth: dateOfBirth ?? undefined,
            dateOfDeath: dateOfDeath ?? undefined,
            profilePicture,
            theme: themeName,
            sections: sections.map(
                section => omit(section, ['sectionID', 'collapse', 'uncollapse']) as APISection
            )
        }
        apiClient.saveProfile(profileID ?? "", profile)
            .then(() => true)
            .catch(async (e) => {
                if (e instanceof APIError && e.statusCode == 412) { // precondition failed
                    if (await confirm?.(<ConfirmOverride/>)) {
                        return apiClient.saveProfile(profileID ?? "", omit(profile, ["etag"])).then(() => true)
                    } else {
                        return false
                    }
                }
                throw e
            })
            .then((successful: boolean) => {
                if (successful) {
                    setEditMode(false)
                    success?.("De herinneringspagina is succesvol opgeslagen")
                }
            })
            .catch(() => error?.("Er is een fout opgetreden bij het opslaan van het profiel"))
            .finally(() => setSaving(false))
    }, [etag, name, dateOfBirth, dateOfDeath, profilePicture, themeName, sections, profileID, confirm, error, success])

    const newSectionProps = useCallback(() => ({
        sectionID: randomString(),
        uncollapse: true,
        collapse: false,
        isPublic: false,
    }), [])
    const addTextSection = useCallback(() => setSections((prev) => [...prev, {
        ...newSectionProps(),
        type: "text",
        text: ""
    }]), [newSectionProps])
    const addPhotoCarousel = useCallback(() => setSections((prev) => [...prev, {
        ...newSectionProps(),
        type: "photo_carousel",
        photos: [],
    }]), [newSectionProps])
    const addVideo = useCallback(async () => {
        if (plan == Plan.Trial && !await confirm?.(<TrialConfirmation type="video"/>)) {
            return
        }
        setSections((prev) => [...prev, {
            ...newSectionProps(),
            type: "video"
        }])
    }, [confirm, newSectionProps, plan])
    const addAudio = useCallback(async () => {
        if (plan == Plan.Trial && !await confirm?.(<TrialConfirmation type="audio"/>)) {
            return
        }
        setSections((prev) => [...prev, {
            ...newSectionProps(),
            type: "audio"
        }])
    }, [confirm, newSectionProps, plan])

    const closeMenu = useCallback(() => menuRef.current?.close(), [])
    const handleClickEdit = useCallback(() => {
        closeMenu();
        enableEditMode();
    }, [closeMenu, enableEditMode])

    const availablePlans: Plan[] = useMemo(() => {
        if (plan != Plan.Trial) {
            return []
        }
        if (sections.find((section) => section.type == "video" || section.type == "audio") !== undefined) {
            return [Plan.Silver]
        }
        return [Plan.Bronze, Plan.Silver]
    }, [plan, sections])

    return loading ? (
        <ProfileLoader/>
    ) : err == 'not_found' || !profileID ? (
        <ProfileNotFound/>
    ) : err == 'unknown' ? (
        <ProfileUnknownError/>
    ) : (
        <>
            <Menu ref={menuRef}>
                {state == "authenticated" ? (
                    <List>
                        <HomeButton/>
                        <QRCodeButton profileID={profileID} onClick={closeMenu}/>
                        <PhotoWithQRCodeButton profileID={profileID} onClick={closeMenu}/>
                    </List>
                ) : null}
                {isAdmin ? (
                    <>
                        <Divider>Pagina beheren</Divider>
                        <List>
                            {!editMode && !isBlocked ? (
                                <ListItem disablePadding>
                                    <ListItemButton onClick={handleClickEdit}>
                                        <ListItemIcon>
                                            <ModeEditIcon/>
                                        </ListItemIcon>
                                        <ListItemText primary="Bewerken"/>
                                    </ListItemButton>
                                </ListItem>
                            ) : null}
                            <GroupMembershipButton profileID={profileID} onClick={closeMenu}/>
                            {availablePlans.length > 0 ? (
                                <SubscriptionButton profileID={profileID} availablePlans={availablePlans} onClick={closeMenu}/>
                            ) : null}

                            <PublicToggle
                                profileID={profileID}
                                isPublic={isPublic}
                                onChange={setIsPublic}
                                disabled={plan == Plan.Trial}
                            />
                            <Divider>Delen</Divider>
                            <ShareButtons
                                name={name ?? '?'}
                                isPublic={isPublic}
                            />
                        </List>
                    </>
                ) : null}
            </Menu>
            <Stack>
                <Box display="flex" flexDirection="row" justifyContent="center">
                    <ProfilePicture
                        profileID={profileID}
                        src={profilePicture || PROFILE_PICTURE_PLACEHOLDER}
                        onChange={setProfilePicture}
                        editMode={editMode}
                    />
                </Box>
                <Summary
                    name={name}
                    onChangeName={setName}
                    dateOfBirth={dateOfBirth}
                    onChangeDateOfBirth={setDateOfBirth}
                    dateOfDeath={dateOfDeath}
                    onChangeDateOfDeath={setDateOfDeath}
                    editMode={editMode}
                />
                {isBlocked ? (
                    <ProfileBlocked/>
                ) : sections.map((section, i) => {
                    const sectionProps = {
                        isFirst: i == 0,
                        isLast: i == sections.length - 1,
                        uncollapse: section.uncollapse,
                        collapse: section.collapse,
                        sectionID: section.sectionID,
                        isPublic: section.isPublic,
                        editMode,
                        onMoveUp: handleSectionMoveUp,
                        onMoveDown: handleSectionMoveDown,
                        onDelete: handleSectionDelete,
                        onCollapsed: handleSectionCollapsed,
                    }

                    return isTextSection(section) ? (
                        <TextSection
                            key={section.sectionID}
                            contents={section.text}
                            onChange={handleSectionTextChanged}
                            onChangeIsPublic={handleSectionChangeIsPublic}
                            {...sectionProps}
                        />
                    ) : isPhotoCarouselSection(section) ? (
                        <CarouselSection
                            key={section.sectionID}
                            profileID={profileID}
                            photos={section.photos}
                            onChange={handleSectionCarouselChanged}
                            onChangeIsPublic={handleSectionChangeIsPublic}
                            {...sectionProps}
                        />
                    ) : isVideoSection(section) ? (
                        <VideoSection
                            key={section.sectionID}
                            profileID={profileID}
                            video={section.video}
                            onChange={handleSectionVideoChanged}
                            onChangeIsPublic={handleSectionChangeIsPublic}
                            {...sectionProps}
                        />
                    ) : isAudioSection(section) ? (
                        <AudioSection
                            key={section.sectionID}
                            profileID={profileID}
                            src={section.src}
                            onChange={handleSectionAudioChanged}
                            onChangeIsPublic={handleSectionChangeIsPublic}
                            {...sectionProps}
                        />
                    ) : null
                })}
                {plan && editMode ? (
                    <AddSectionButtonGrid
                        plan={plan}
                        onAddText={addTextSection}
                        onAddPhotoCarousel={addPhotoCarousel}
                        onAddVideo={addVideo}
                        onAddAudio={addAudio}
                    />
                ) : null}
            </Stack>
            {editMode ? (
                <EditBottomBar
                    theme={themeName}
                    onChangeTheme={setThemeByName}
                    onCancel={disableEditMode}
                    onSave={handleSave}
                    saving={saving}
                />
            ) : null}
        </>
    )
}

export default MemorialPage
